import * as THREE from 'three';
import type { SketchFace, SketchVec3 } from './types';
import { GLYPHS, GLYPH_ADVANCE, GLYPH_EM_HEIGHT } from '../geometry/nurbs/textFont';

/**
 * 3D Text builder — extruded vector text as one sketch solid.
 *
 * The built-in stroke font (core/geometry/nurbs/textFont) defines each
 * glyph as polyline strokes on a 6×8 em grid. Every stroke segment becomes
 * a closed rectangular bar (front face + back face + four walls) extruded
 * along the placement plane's normal, so the result is a fully analytic
 * shell the ordinary face tools keep working on.
 */

const asTuple = (p: THREE.Vector3): SketchVec3 => [p.x, p.y, p.z];

/** One closed bar between a 2D rectangle (CCW seen from +n) and depth. */
function barFaces(prefix: string, loop: THREE.Vector3[], n: THREE.Vector3, depth: number, materialId: string): SketchFace[] {
    const half = depth / 2;
    const front = loop.map((p) => p.clone().addScaledVector(n, half));
    const back = loop.map((p) => p.clone().addScaledVector(n, -half));
    const face = (pts: THREE.Vector3[], normal: THREE.Vector3, tag: string): SketchFace => ({
        id: `${prefix}-${tag}`,
        origin: asTuple(pts[0]),
        normal: asTuple(normal),
        outer: pts.map(asTuple),
        holes: [],
        materialId,
    });
    const faces: SketchFace[] = [face(front, n.clone(), 'front')];
    const backNormal = n.clone().negate();
    faces.push(face([...back].reverse(), backNormal, 'back'));
    for (let i = 0; i < loop.length; i++) {
        const aB = back[i];
        const bB = back[(i + 1) % loop.length];
        const bF = front[(i + 1) % loop.length];
        const aF = front[i];
        const outward = new THREE.Vector3().crossVectors(bB.clone().sub(aB), n).normalize();
        faces.push(face([aB, bB, bF, aF], outward, `side-${i}`));
    }
    return faces;
}

export interface Text3DOptions {
    text: string;
    height: number;
    depth: number;
    /** Baseline-left anchor of the text block. */
    origin: THREE.Vector3;
    /** Placement plane normal (text grows along the plane's u axis). */
    normal: THREE.Vector3;
    materialId?: string;
    strokeRatio?: number;
}

/** Build a 3D text solid; empty/unsupported text yields null. */
export function buildText3DSolid(options: Text3DOptions): { name: string; faces: SketchFace[] } | null {
    const text = options.text.trim();
    if (!text) return null;
    const n = options.normal.clone().normalize();
    const ref = Math.abs(n.z) < 0.9 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(1, 0, 0);
    const u = new THREE.Vector3().crossVectors(ref, n).normalize();
    const v = new THREE.Vector3().crossVectors(n, u).normalize();
    const s = options.height / GLYPH_EM_HEIGHT;
    const stroke = options.height * (options.strokeRatio ?? 0.16);
    const materialId = options.materialId ?? 'concrete';

    const faces: SketchFace[] = [];
    let pen = 0;
    let emitted = false;
    for (const ch of text.toUpperCase()) {
        const strokes = GLYPHS[ch] ?? GLYPHS['?'] ?? [];
        for (const strokePts of strokes) {
            let prev: THREE.Vector2 | null = null;
            for (const p of strokePts) {
                const q = new THREE.Vector2((pen + p[0]) * s, p[1] * s);
                if (prev) {
                    const dir = q.clone().sub(prev);
                    const len = dir.length();
                    if (len > 1e-9) {
                        dir.normalize();
                        const perp = new THREE.Vector2(-dir.y, dir.x).multiplyScalar(stroke / 2);
                        const at = (pt: THREE.Vector2, offset: THREE.Vector2, lift: number): THREE.Vector3 =>
                            options.origin.clone()
                                .addScaledVector(u, pt.x + offset.x)
                                .addScaledVector(v, pt.y + offset.y)
                                .addScaledVector(n, lift);
                        // CCW seen from +n: upper-left → upper-right → lower-right → lower-left.
                        const loop = [
                            at(prev, perp.clone().negate(), 0),
                            at(q, perp.clone().negate(), 0),
                            at(q, perp, 0),
                            at(prev, perp, 0),
                        ];
                        faces.push(...barFaces(`t3d-${faces.length}`, loop, n, options.depth, materialId));
                        emitted = true;
                    }
                }
                prev = q;
            }
        }
        pen += GLYPH_ADVANCE;
    }
    if (!emitted) return null;
    return { name: `3D Text "${text}"`, faces };
}
